'use client';

import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import Image from 'next/image';
import NextLink from 'next/link';
import type { Link } from '@/blocks/components/types';
import type { SxProps, Theme } from '@mui/material/styles';

interface ProductCardProps {
  image: string;
  title: string;
  subtitle?: string;
  link: Link;
  color?: string;
  sx?: SxProps<Theme>;
}

export default function ProductCard({
  image,
  title,
  subtitle,
  link,
  color = 'common.white',
  sx = {},
}: ProductCardProps) {
  const isClickable = link.visible && link.href;

  const cardSx: SxProps<Theme> = {
    position: 'relative',
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    aspectRatio: '9/16',
    mt: 0.75,
    mb: 2,
    p: 0,
    cursor: isClickable ? 'pointer' : 'default',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '& img': {
      transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    '&:hover': isClickable
      ? {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        }
      : {},
    '&:hover img': isClickable ? { transform: 'scale(1.04)' } : {},
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '& img': { transition: 'none' },
      '&:hover': { transform: 'none' },
      '&:hover img': { transform: 'none' },
    },
    ...sx,
  };

  const cardContent = (
    <Card sx={cardSx}>
      {/* Background Image with Overlay */}
      {image && (
        <Image
          src={image}
          alt={title}
          fill
          sizes='(max-width: 600px) 100vw, (max-width: 1024px) 50vw, 33vw'
          style={{ objectFit: 'cover' }}
        />
      )}

      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          right: 8,
          pointerEvents: 'none',
          p: 0.75,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {/* Title */}
        <Typography
          variant='h4'
          component='h2'
          sx={{
            color,
            fontWeight: 'bold',
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
            lineHeight: 1.1,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant='subtitle1'
            sx={{
              color,
              fontWeight: 'regular',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: 1.1,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Corner Button */}
      {isClickable && (
        <Button
          sx={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            border: '2px solid',
            borderColor: color,
            color,
            borderRadius: 10,
            zIndex: 1,
          }}
          variant='outlined'
          size='medium'
          aria-label={`Navigate to ${title}`}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
        >
          <EastRoundedIcon sx={{ fontSize: 24 }} />
        </Button>
      )}
    </Card>
  );

  return isClickable ? (
    <NextLink href={link.href} style={{ textDecoration: 'none' }}>
      {cardContent}
    </NextLink>
  ) : (
    cardContent
  );
}
